import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessStore, type Business } from '../stores/business';

export default function BusinessesPage() {
  const navigate = useNavigate();
  const {
    businesses,
    isLoadingBusinesses,
    error,
    fetchBusinesses,
    createBusiness,
    deleteBusiness,
    setCurrentBusiness,
    clearError,
  } = useBusinessStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    ghlPitToken: '',
    ghlLocationId: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const business = await createBusiness({
        name: formData.name,
        slug: formData.slug,
        ghlPitToken: formData.ghlPitToken || undefined,
        ghlLocationId: formData.ghlLocationId || undefined,
      });
      setShowCreateModal(false);
      setFormData({ name: '', slug: '', ghlPitToken: '', ghlLocationId: '' });
      setCurrentBusiness(business.id);
      navigate(`/businesses/${business.id}/agents`);
    } catch {
      // Error handled by store
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"? This will delete all agents and data.`)) {
      await deleteBusiness(id);
    }
  };

  const handleSelectBusiness = (business: Business) => {
    setCurrentBusiness(business.id);
    navigate(`/businesses/${business.id}/agents`);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <div className="min-h-screen bg-surface-950 p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-white">Businesses</h1>
            <p className="text-surface-400 mt-1">Manage your businesses and their agents</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Business
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-500/10 border border-red-500/20 p-4 flex items-center justify-between">
            <span className="text-red-400">{error}</span>
            <button onClick={clearError} className="text-red-400 hover:text-red-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoadingBusinesses && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingBusinesses && businesses.length === 0 && (
          <div className="text-center py-16 bg-surface-900/50 rounded-xl border border-surface-800">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-surface-800 mb-4">
              <svg className="w-8 h-8 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No businesses yet</h3>
            <p className="text-surface-400 mb-6">Create your first business to get started with AI voice agents.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First Business
            </button>
          </div>
        )}

        {/* Business Grid */}
        {!isLoadingBusinesses && businesses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((business) => (
              <div
                key={business.id}
                className="card hover:border-primary-500/50 transition-colors cursor-pointer group"
                onClick={() => handleSelectBusiness(business)}
              >
                <div className="card-body">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-lg">
                        {business.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white group-hover:text-primary-400 transition-colors">
                          {business.name}
                        </h3>
                        <p className="text-sm text-surface-500">/{business.slug}</p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        business.isActive
                          ? 'bg-green-500/10 text-green-400'
                          : 'bg-surface-700 text-surface-400'
                      }`}
                    >
                      {business.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-surface-400">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>Role: {business.memberRole || 'owner'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-surface-400">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <span className={business.ghlLocationId ? 'text-green-400' : ''}>
                        {business.ghlLocationId ? 'GHL Connected' : 'GHL Not Configured'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-surface-800 flex items-center justify-between">
                    <span className="text-xs text-surface-500">
                      Created {new Date(business.createdAt).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/businesses/${business.id}/settings`);
                        }}
                        className="text-surface-500 hover:text-primary-400 transition-colors"
                        title="Settings"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(business.id, business.name);
                        }}
                        className="text-surface-500 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-lg">
            <div className="card-body">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Create New Business</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-surface-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: generateSlug(e.target.value),
                      });
                    }}
                    className="input"
                    placeholder="My Real Estate Company"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    URL Slug *
                  </label>
                  <div className="flex items-center">
                    <span className="text-surface-500 mr-2">/</span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="input"
                      placeholder="my-company"
                      pattern="[a-z0-9\-]+"
                      required
                    />
                  </div>
                  <p className="text-xs text-surface-500 mt-1">
                    Lowercase letters, numbers, and hyphens only
                  </p>
                </div>

                <div className="border-t border-surface-800 pt-4 mt-4">
                  <h3 className="text-sm font-medium text-surface-300 mb-3">
                    GoHighLevel Integration (Optional)
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-400 mb-2">
                        API Token (PIT)
                      </label>
                      <input
                        type="password"
                        value={formData.ghlPitToken}
                        onChange={(e) => setFormData({ ...formData, ghlPitToken: e.target.value })}
                        className="input"
                        placeholder="pit-xxxxxxxx..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-400 mb-2">
                        Location ID
                      </label>
                      <input
                        type="text"
                        value={formData.ghlLocationId}
                        onChange={(e) => setFormData({ ...formData, ghlLocationId: e.target.value })}
                        className="input"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="btn-primary flex-1"
                  >
                    {isCreating ? 'Creating...' : 'Create Business'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

